import { useState } from "react";
import { toast } from "sonner";
import { Radio, AlertTriangle } from "lucide-react";
import { api, errMsg, useApi, ago } from "../../lib/api";
import { useAuth, hasRole } from "../../context/AuthContext";
import { StatusDot, SampleBadge, Decision } from "../Badges";

const STATIONS = ["", "Peace FM", "UTV", "Okay FM", "Neat FM", "Hello FM", "Peacefmonline.com", "Despite Media Group"];
const STATUSES = ["", "on_air", "available", "on_assignment", "on_leave", "off_duty"];

const StaffCard = ({ s, canEdit, onStatus }) => (
  <div className="panel p-4 flex flex-col gap-2" data-testid={`staff-card-${s.id}`}>
    <div className="flex items-start justify-between gap-2">
      <div>
        <div className="text-ink font-semibold">{s.name}</div>
        {s.alias && <div className="text-xs text-gold">“{s.alias}”</div>}
        <div className="text-xs text-slate-400">{s.role} · {s.department}</div>
      </div>
      {s.on_air_now ? <span className="pill pill-red"><Radio size={10} /> On air</span> : <StatusDot status={s.effective_status} />}
    </div>
    <div className="font-data text-[10px] text-slate-500 uppercase">{s.station} · {s.shift}</div>
    <div>
      <div className="flex justify-between text-[10px] font-data text-slate-500"><span>Load</span><span>{s.open_tasks}/{s.capacity} tasks</span></div>
      <div className="h-1 bg-slate-800 rounded-full mt-1"><div className={`h-1 rounded-full ${s.load >= 1 ? "bg-red-500" : s.load >= 0.5 ? "bg-amber-500" : "bg-green-500"}`} style={{ width: `${Math.min(s.load, 1) * 100}%` }} /></div>
    </div>
    <div className="flex flex-wrap gap-1">
      {s.is_sample && <SampleBadge label="Sample record" />}
      {s.inactive_30d && <span className="pill pill-amber"><AlertTriangle size={10} /> Inactive 30d+</span>}
    </div>
    {s.notable && <p className="text-[11px] text-slate-500">{s.notable}</p>}
    <div className="text-[10px] text-slate-600 font-data">Last active {ago(s.last_active)} · {s.contact}</div>
    {canEdit && (
      <select className="field !py-1.5 !text-xs" value={s.status} onChange={(e) => onStatus(s.id, e.target.value)} data-testid={`staff-status-select-${s.id}`}>
        {STATUSES.slice(2).concat("available").filter((v, i, a) => a.indexOf(v) === i).map((x) => <option key={x} value={x}>{x.replace("_", " ")}</option>)}
      </select>
    )}
  </div>
);

export const Directory = () => {
  const { user } = useAuth();
  const [flt, setFlt] = useState({ q: "", station: "", status: "" });
  const qs = new URLSearchParams(Object.entries(flt).filter(([, v]) => v)).toString();
  const { data, reload } = useApi(`/staff?${qs}`);
  const mgr = hasRole(user, ["manager", "hr", "gm", "executive"]);
  const onStatus = async (id, status) => {
    try {
      await api.patch(`/staff/${id}/status`, { status });
      toast.success("Status updated");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const set = (k) => (e) => setFlt({ ...flt, [k]: e.target.value });
  return (
    <div data-testid="staff-directory">
      <div className="grid sm:grid-cols-3 gap-3 mb-6">
        <input className="field" placeholder="Search name, alias, role…" value={flt.q} onChange={set("q")} data-testid="staff-search-input" />
        <select className="field" value={flt.station} onChange={set("station")} data-testid="staff-filter-station">{STATIONS.map((s) => <option key={s} value={s}>{s || "All stations"}</option>)}</select>
        <select className="field" value={flt.status} onChange={set("status")} data-testid="staff-filter-status">{STATUSES.map((s) => <option key={s} value={s}>{s ? s.replace("_", " ") : "Any availability"}</option>)}</select>
      </div>
      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {(data || []).map((s) => <StaffCard key={s.id} s={s} canEdit={mgr || user?.staff_id === s.id} onStatus={onStatus} />)}
      </div>
      <Decision>Who is on air right now, who can take an urgent assignment, and who has gone quiet for 30+ days.</Decision>
    </div>
  );
};
